import {
  Box,
  IconButton,
  Skeleton,
  Typography,
  useMediaQuery,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import React, { MouseEventHandler } from "react";
import clsx from "clsx";
import { colorSystem } from "../../theme";
import { Pencil, Settings } from "lucide-react";
import { ReactComponent as ArrowIcon } from "../../icons/arrowGray.svg";

const useStyles = makeStyles((theme) => ({
  header: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    marginBottom: theme.spacing(3),
  },

  topRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    width: "100%",
    position: "relative",
  },

  collapsedHeader: {
    marginBottom: 0,
  },

  headerIcon: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
  },
  headerTextBlock: {
    display: "flex",
    flexDirection: "column",
  },

  title: {
    fontSize: 16,
    fontWeight: 600,
    lineHeight: "20px",
    color: theme.palette.common.black,
  },

  subtitle: {
    fontSize: 14,
    fontWeight: 500,
    lineHeight: "18px",
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },

  todayContainer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginTop: theme.spacing(3),
  },

  today: {
    fontSize: 12,
    fontWeight: 700,
    lineHeight: "12px",
    color: theme.palette.common.black,
    backgroundColor: colorSystem.gray9,
    borderRadius: 4,
    padding: theme.spacing(0.2, 0.8),
    textTransform: "uppercase",
  },

  todayValue: {
    fontSize: 20,
    fontWeight: 700,
    lineHeight: "24px",
    color: theme.palette.common.black,
  },

  iconButtonGroup: {
    display: "flex",
    alignItems: "center",
    position: "absolute",
    top: theme.spacing(2),
    right: 0,
  },

  iconButton: {
    margin: theme.spacing(-10, 0),
  },

  arrowIcon: {
    marginLeft: theme.spacing(2),
  },

  arrowIconDown: {
    transform: "rotate(180deg)",
  },

  arrowIconRight: {
    transform: "rotate(90deg)",
    marginRight: theme.spacing(1),
  },
}));

interface ClientDetailsCardHeaderProps {
  icon: React.ReactNode;
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  todayValue?: string;
  onEditClick?: MouseEventHandler;
  disableOnEdit?: boolean;
  onSettingsClick?: MouseEventHandler;
  disableSettings?: boolean;
  disableCompactEdit?: boolean;
  isOpenContent?: boolean;
  setIsOpenContent?: (value: (val: boolean) => boolean) => void;
  children?: React.ReactNode;
  showSubtitleSkeleton?: boolean;
  showTodayValueSkeleton?: boolean;
}

export default function ClientDetailsCardHeader(
  props: ClientDetailsCardHeaderProps,
) {
  const {
    icon,
    title,
    subtitle,
    todayValue,
    onEditClick,
    onSettingsClick,
    disableOnEdit,
    disableSettings,
    disableCompactEdit,
    isOpenContent,
    setIsOpenContent,
    children,
    showSubtitleSkeleton,
    showTodayValueSkeleton,
  } = props;

  const smUp = useMediaQuery((theme) => theme.breakpoints.up("sm"));

  const iconSize = smUp ? 18 : 24;
  const s = useStyles({ iconSize });

  const canShowCompactEdit =
    onEditClick &&
    !onSettingsClick &&
    !setIsOpenContent &&
    !smUp &&
    !disableCompactEdit;

  React.useEffect(() => {
    if (setIsOpenContent) {
      !smUp ? setIsOpenContent((_) => false) : setIsOpenContent((_) => true);
    }
  }, [smUp, setIsOpenContent]);

  return (
    <Box
      className={clsx(
        s.header,
        setIsOpenContent && !isOpenContent && s.collapsedHeader,
      )}
      onClick={(e) => {
        if (setIsOpenContent && !smUp) {
          setIsOpenContent((open) => !open);
          e.stopPropagation();
        }
      }}
    >
      <Box className={s.topRow}>
        <Box>
          <Box className={s.headerIcon}>
            {icon}
            <Box className={s.headerTextBlock}>
              <Typography variant="h6" className={s.title} children={title} />
              {showSubtitleSkeleton ? (
                <Skeleton width={120} height={20} />
              ) : subtitle ? (
                <Typography
                  variant="body1"
                  className={s.subtitle}
                  children={subtitle}
                />
              ) : null}
            </Box>
          </Box>
          {(showTodayValueSkeleton || todayValue) && (
            <Box className={s.todayContainer}>
              {showTodayValueSkeleton ? (
                <>
                  <Skeleton width={120} height={24} />
                </>
              ) : (
                <>
                  <Typography className={s.todayValue}>{todayValue}</Typography>
                  <Typography className={s.today}>TODAY</Typography>
                </>
              )}
            </Box>
          )}
        </Box>

        {children}

        <Box className={s.iconButtonGroup}>
          {canShowCompactEdit ? (
            <ArrowIcon
              className={clsx(s.arrowIcon, s.arrowIconRight)}
              onClick={(e) => {
                onEditClick(e);
                e.stopPropagation();
              }}
            />
          ) : (
            onEditClick && (
              <IconButton
                disabled={disableOnEdit}
                className={s.iconButton}
                onClick={(e) => {
                  onEditClick(e);
                  e.stopPropagation();
                }}
              >
                <Pencil size={iconSize} />
              </IconButton>
            )
          )}
          {onSettingsClick && (
            <IconButton
              disabled={disableSettings}
              onClick={(e) => {
                onSettingsClick(e);
                e.stopPropagation();
              }}
              className={s.iconButton}
            >
              <Settings size={iconSize} />
            </IconButton>
          )}
          {setIsOpenContent && !smUp && (
            <ArrowIcon
              className={clsx(s.arrowIcon, !isOpenContent && s.arrowIconDown)}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
}
